/**
 * useSignupState — hydrates the wizard from server-side state on mount,
 * so a user who closes their tab mid-wizard resumes at the right step.
 *
 * Returns a SignupState shape: {currentStep, completedSteps[], loading,
 * error}. The orchestrator initialises its render at currentStep.
 *
 * If the API errors (network blip on first render), we fall back to
 * step 1 (practice_area) so the user can still proceed — better to lose
 * resume-state than block onboarding entirely.
 */
import { useEffect, useState } from 'react';
import { signupApi, StepName, STEP_ORDER } from './signupApi';

interface SignupState {
  loading: boolean;
  error: string | null;
  /** First incomplete required step. null when all done (caller should /finalize). */
  currentStep: StepName | null;
  /** Required steps the user has completed or skipped, in canonical order. */
  completedSteps: StepName[];
  /** True when the backend says the user is already onboarded — gate logic should NOT show the wizard. */
  isOnboarded: boolean;
  /** Navigate the wizard to a specific step — used by the back button. */
  setCurrentStep: (s: StepName) => void;
  /** Mark a step as completed locally (called by the orchestrator after telemetry success). */
  markCompleted: (s: StepName) => void;
}

export function useSignupState(): SignupState {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<StepName | null>('practice_area');
  const [completedSteps, setCompletedSteps] = useState<StepName[]>([]);
  const [isOnboarded, setIsOnboarded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    signupApi
      .state()
      .then((res) => {
        if (cancelled) return;
        const { current_step, completed_steps, is_onboarded } = res.data;
        // Preserve null: it means "all steps done, finalize now" — coercing it
        // to step 1 here is what used to trap users in the wizard forever.
        setCurrentStep(current_step);
        setCompletedSteps(completed_steps);
        setIsOnboarded(is_onboarded);
      })
      .catch((err) => {
        if (cancelled) return;
        // Don't block the wizard on a state-fetch error — start fresh at step 1.
        console.warn('[signup] state fetch failed; defaulting to step 1:', err);
        setError(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const markCompleted = (s: StepName) => {
    // Compute the merged set once so the next-step decision can't read a
    // stale completedSteps closure (the old code skipped or repeated steps).
    const merged = completedSteps.includes(s) ? completedSteps : [...completedSteps, s];
    // Maintain canonical order so the progress bar renders correctly.
    setCompletedSteps(STEP_ORDER.filter((step) => merged.includes(step)));
    // Advance to the first remaining incomplete step (or null if all done).
    const remaining = STEP_ORDER.find((step) => !merged.includes(step));
    setCurrentStep(remaining ?? null);
  };

  return {
    loading,
    error,
    currentStep,
    completedSteps,
    isOnboarded,
    setCurrentStep,
    markCompleted,
  };
}
